import { types, flow, applySnapshot, getSnapshot } from 'mobx-state-tree';
import { pick } from 'lodash';

import { makeRequest, errorResponse } from 'js/services/Network';
import PlaidLink from 'js/services/PlaidLink';
import BankAccount from 'js/stores/types/BankAccount';
import CreditCard from "js/stores/types/CreditCard";

export default types
  .model('ReceivingAccount', {
    isSaving: false,
    isVerifying: false,
    hasClearedNumber: false,
    modalIsVisible: false,
    choicesModalIsVisible: false,
    verifyModalIsVisible: false,
    verifyChoicesModalIsVisible: false,
    error: types.maybeNull(types.string),
    verifyError: types.maybeNull(types.string),
    plaidError: types.maybeNull(types.string),
    bankAccount: types.optional(BankAccount, {}),
    debitCard: types.optional(CreditCard, {}),
    debit_card_error: types.optional(types.union(types.string, types.null), null),
    isSavingCard: false,
    plaidIsStarted: false,
    plaidIsUpdating: false,
    savingPlaidItem: false,
  })
  .views(self => ({
    get isBusy() {
      return self.plaidIsStarted || self.plaidIsUpdating || self.savingPlaidItem || self.isSaving || self.isVerifying;
    },
  }))
  .actions(self => ({
    clearNumber() {
      if (!self.hasClearedNumber) {
        self.bankAccount.setNumber('');
        self.hasClearedNumber = true;
      }
    },
    showChoicesModal() {
      self.choicesModalIsVisible = true;
    },
    showBankAccountModal() {
      if (app.bank_verification_flow === 'plaid' && self.bankAccount.bank_verification_flow === 'system') {
        // Verify/connect bank account using Plaid
        self.startPlaid();
      } else {
        // Verify/connect bank account using old flow
        self.modalIsVisible = true;
      }

      return self;
    },
    closeModals() {
      self.modalIsVisible = false;
      self.verifyModalIsVisible = false;
      self.choicesModalIsVisible = false;
      self.verifyChoicesModalIsVisible = false;
      return self;
    },
    onComplete(data) {
      self.modalIsVisible = false;
      applySnapshot(self, { ...getSnapshot(self), ...data });
      return self;
    },

    onVerify() {
      // Show the verify microdeposits modal
      if (self.bankAccount.onboarding_service === 'plaid') {
        // Use Plaid update flow
        self.updatePlaidItem();
      } else {
        // Old flow using internally triggered microdeposits
        self.verifyModalIsVisible = true;
      }
    },

    saveBankAccount: flow(function* saveBankAccount() {
      // Save a regular bank account entry from the old modal UI
      let response = null;
      if (!self.isBusy) {
        self.error = null;
        self.isSaving = true;

        try {
          response = yield makeRequest('bank-save', null, pick(self.bankAccount, ['route', 'number', 'type']));
        } catch (error) {
          self.error = errorResponse(error);
          self.isSaving = false;
        }

        self.isSaving = false;
      }
      return response ? response.data : null;
    }),

    verifyBankAccount: flow(function* verifyBankAccount(event, microdeposits) {
      // Submit a microdeposits verification attempt
      let response = null;
      if (!self.isBusy) {
        self.verifyError = null;
        self.isVerifying = true;

        try {
          self.bankAccount = (yield makeRequest('bank-verify', self.bankAccount, microdeposits)).data;
        } catch (error) {
          self.verifyError = errorResponse(error);
        }

        if (self.bankAccount.is_verified || self.bankAccount.verification_denial_reason) {
          self.verifyModalIsVisible = false;
        } else {
          self.verifyError = 'Submitted verification values incorrect';
        }

        self.isVerifying = false;
      }
      return response ? response.data : null;
    }),

    startPlaid: flow(function* startPlaid() {
      // Start the Plaid bank account entry flow
      if (!self.isBusy) {
        let plaidItem;
        self.plaidError = null;
        self.plaidIsStarted = true;

        const scrollX = window.scrollX;
        const scrollY = window.scrollY;
        try {
          plaidItem = (yield new PlaidLink(app.plaid).open()).metadata;
        } catch (error) {}
        window.scrollTo(scrollX, scrollY);
        self.plaidIsStarted = false;

        if (plaidItem) {
          self.savingPlaidItem = true;
          try {
            self.bankAccount = (yield makeRequest(
              'plaid-item-save',
              null,
              pick(plaidItem, ['account', 'institution', 'public_token', 'link_session_id']),
            )).data;
          } catch (error) {
            self.plaidError = errorResponse(error);
          }
          self.savingPlaidItem = false;
        }
      }
    }),

    updatePlaidItem: flow(function* updatePlaidItem() {
      // Start the Plaid bank account update flow (verify microdeposits, MFA)
      if (!self.isBusy) {
        let itemUpdate = null;
        let token = null;

        self.plaidError = null;
        self.plaidIsUpdating = true;

        try {
          token = (yield makeRequest('generate-plaid-token', self.bankAccount)).data;
        } catch (error) {
          self.plaidError = errorResponse(error);
          window.app.env !== 'production' && console.error(error);
        }

        if (token) {
          const scrollX = window.scrollX;
          const scrollY = window.scrollY;
          try {
            itemUpdate = yield new PlaidLink({
              token,
              ...app.plaid,
            }).open();
          } catch (error) {
            if (error.err && self.bankAccount.report_update_errors.includes(error.err.error_code)) {
              itemUpdate = error;
            }
            window.app.env !== 'production' && console.info('Plaid exit', error);
          }
          window.scrollTo(scrollX, scrollY);
        }

        self.plaidIsUpdating = false;

        if (itemUpdate) {
          self.savingPlaidItem = true;
          try {
            self.bankAccount = (yield makeRequest('plaid-item-verify', self.bankAccount, itemUpdate)).data;
          } catch (error) {
            self.plaidError = errorResponse(error);
            window.app.env !== 'production' && console.error(error);
          }
          self.savingPlaidItem = false;
        }
      }
    }),

    saveDebitCard: flow(function* saveDebitCard(values) {
      let response;

      if (!self.isSavingCard) {
        self.debit_card_error = null;
        self.isSavingCard = true;
        try {
          response = yield makeRequest('debit-card-save', null, values);
          applySnapshot(self, { ...getSnapshot(self), ...response.data });
          self.debitCard.setId(response.data.id);
          self.closeModals();
        } catch (error) {
          self.debit_card_error = errorResponse(error);
        }
      }
      self.isSavingCard = false;

      return response;
    }),

  }));
